import React, { useCallback, useContext, useEffect, useState } from "react";

import {
  Autocomplete,
  FormControl,
  Grid,
  IconButton,
  Paper,
  Select,
  Snackbar,
  SnackbarCloseReason,
  Tooltip
} from "@mui/material";
import Alert from "@mui/material/Alert";
import { Controller, useForm } from "react-hook-form";

import { methods } from "api/methods";
import { Nutrient } from "api/models";
import { resources } from "api/resources";
import { useApi } from "api/useApi";
import { Header2, PageLoadStateContext } from "components/common";
import { FormHelperText, InputLabel, TextField } from "components/common/";
import ConfirmationModal from "components/common/ConfirmationModal";
import InfoIcon from "components/common/Icon/InfoIcon";
import PrimaryButton from "components/common/PrimaryButton";
import { useTranslations } from "components/useTranslations";

import useNutrientDetailsFormReducer, {
  NutrientDetailsFormActionPayloadType,
  NutrientDetailsFormActionType,
  ValidationDetails
} from "./useNutrientDetailsFormReducer";
import useNutrientsDetailsFormStyles from "./useNutrientsDetailsFormStyles";

import { useTheme } from "../../../../theme/index";

export type NutrientsDetailsFormProps = {
  nutrient?: Nutrient;
  onNameSaved?: (name: string) => void;
};

const NutrientsDetailsForm: React.FC<NutrientsDetailsFormProps> = props => {
  const styles = useNutrientsDetailsFormStyles();
  const execute = useApi();
  const { nutrient, onNameSaved } = props;
  const t = useTranslations();
  const pageLoadState = useContext(PageLoadStateContext) ?? { loaded: true };
  const [confirmationModalIsOpen, setConfirmationModalIsOpen] = useState(false);
  const [state, dispatch] = useNutrientDetailsFormReducer();
  const theme = useTheme();
  const {
    control,
    trigger,
    reset,
    formState: { errors }
  } = useForm<Pick<Nutrient, "name" | "code">>({
    defaultValues: {
      name: nutrient?.name ?? "",
      code: nutrient?.code ?? ""
    }
  });

  // State whether form successful
  const [success, setSuccess] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  // State whether form busy
  const [busy, setBusy] = useState(false);

  // State whether form busy
  const [classOptions, setClassOptions] = useState(
    [] as {
      name: string;
      keyValue: string;
    }[]
  );

  useEffect(() => {
    setClassOptions(
      state?.nutrientDetailsDropdownOptions?.families?.find(
        x => x.keyValue === state?.formDetails?.selectedFamily
      )?.classes ?? []
    );
  }, [
    state?.formDetails?.selectedFamily,
    state?.nutrientDetailsDropdownOptions?.families
  ]);

  const setDefaultPackageToNutrientPackages = useCallback(
    (packageKey?: string) => {
      const originalValues: string[] = nutrient?.nutrientPackages
        ? nutrient?.nutrientPackages.slice()
        : [];
      const newValues = originalValues;
      if (packageKey && !originalValues?.includes(packageKey)) {
        newValues.push(packageKey);
      }

      return newValues;
    },
    [nutrient]
  );

  // On page load logic
  useEffect(() => {
    const updatedNutrientPackages = setDefaultPackageToNutrientPackages(
      nutrient?.defaultNutrientPackageKey
    );
    reset({
      name: nutrient?.name ?? "",
      code: nutrient?.code ?? ""
    });
    dispatch({
      type: NutrientDetailsFormActionType.ON_PAGE_LOAD,
      payload: {
        key: nutrient?.key ?? "",
        formDetails: {
          nutrientName: nutrient?.name ?? "",
          nutrientCode: nutrient?.code ?? "",
          nutrientOvsName: nutrient?.ovsName ?? "",
          nutrientUnit: nutrient?.unit ?? "",
          nutrientUnitKey: nutrient?.unitKey ?? "",
          numeratorNutrientKey: nutrient?.numeratorNutrientKey ?? undefined,
          denominatorNutrientKey: nutrient?.denominatorNutrientKey ?? undefined,
          isRatio: nutrient?.isRatio,
          nutrientPackages: updatedNutrientPackages,
          selectedFamily: nutrient?.selectedFamily,
          selectedClass: nutrient?.selectedClass,
          nutrientType: nutrient?.nutrientType,
          nutrientAutoCalcName: nutrient?.autoCalcName ?? ""
        },
        nutrientUnits: nutrient?.unitOptions,
        ratioUnitKey: nutrient?.ratioUnitKey,
        nutrientPackageOptions: nutrient?.nutrientPackageOptions,
        nutrientOptions: nutrient?.nutrientOptions,
        nutrientDetailsDropdownOptions:
          nutrient?.nutrientDetailsDropdownOptions,
        nutrientTypeOptions: nutrient?.nutrientTypeOptions
      }
    });
  }, [nutrient, dispatch, reset, setDefaultPackageToNutrientPackages]);

  const handleMultipleSelectAppendOnly = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const { options: lastSelection } = event.target as HTMLSelectElement;
    const originalValues: string[] = nutrient?.nutrientPackages
      ? nutrient?.nutrientPackages.slice()
      : [];
    const newValues = originalValues;
    for (let i = 0, l = lastSelection.length; i < l; i++) {
      if (lastSelection[i].selected) {
        if (!newValues.includes(lastSelection[i].value)) {
          newValues.push(lastSelection[i].value);
        }
      }
      if (newValues.length === 0 && nutrient?.defaultNutrientPackageKey) {
        newValues.push(nutrient?.defaultNutrientPackageKey);
      }
    }
    dispatch({
      type: NutrientDetailsFormActionType.CHANGE_FORM_STATE,
      payload: {
        key: NutrientDetailsFormActionPayloadType.FORM_DETAILS,
        value: { ...state.formDetails, nutrientPackages: newValues }
      }
    });
    return newValues;
  };

  const handleMultipleSelect = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const { options } = event.target as HTMLSelectElement;
    const selected_values: string[] = [];
    for (let i = 0, l = options.length; i < l; i++) {
      if (options[i].selected) {
        selected_values.push(options[i].value);
      }
      if (selected_values.length === 0 && nutrient?.defaultNutrientPackageKey) {
        selected_values.push(nutrient?.defaultNutrientPackageKey);
      }
    }
    dispatch({
      type: NutrientDetailsFormActionType.CHANGE_FORM_STATE,
      payload: {
        key: NutrientDetailsFormActionPayloadType.FORM_DETAILS,
        value: { ...state.formDetails, nutrientPackages: selected_values }
      }
    });
    return selected_values;
  };

  const clearNameAndCodeValidationErrors = () => {
    setSuccess(false);
    setSaveFailed(false);
    dispatch({
      type: NutrientDetailsFormActionType.CHANGE_FORM_STATE,
      payload: {
        key: NutrientDetailsFormActionPayloadType.VALIDATION_DETAILS,
        value: {
          ...state.validationDetails,
          nutrientNameValidationMessage: "",
          nutrientCodeValidationMessage: ""
        }
      }
    });
  };

  // Save Button
  const onClick = async () => {
    clearNameAndCodeValidationErrors();
    if (!(await trigger())) {
      return;
    }

    if (nutrient?.key) {
      const hasMissingRequiredFields =
        !state.formDetails?.nutrientUnitKey?.trim() ||
        (state.formDetails?.isRatio &&
          (!state.formDetails?.numeratorNutrientKey ||
            !state.formDetails?.denominatorNutrientKey)) ||
        (classOptions.length > 0 && !state.formDetails?.selectedClass);

      if (hasMissingRequiredFields) {
        void onSave();
        return;
      }

      if (!confirmationModalIsOpen) setConfirmationModalIsOpen(true);
    } else {
      void onSave();
    }
  };

  // Save Action
  const onSave = async () => {
    clearNameAndCodeValidationErrors();
    if (!(await trigger())) {
      return;
    }

    setSuccess(false);
    setSaveFailed(false);
    const formValues: Nutrient = {
      key: state.key ?? "",
      name: state.formDetails?.nutrientName ?? "",
      code: state.formDetails?.nutrientCode ?? "",
      ovsName: state.formDetails?.nutrientOvsName ?? "",
      unit: state.formDetails?.nutrientUnit,
      unitKey: state.formDetails?.nutrientUnitKey,
      numeratorNutrientKey: state.formDetails?.numeratorNutrientKey ?? null,
      denominatorNutrientKey: state.formDetails?.denominatorNutrientKey ?? null,
      nutrientPackages: state.formDetails?.nutrientPackages ?? [],
      isRatio: state.formDetails?.isRatio,
      selectedFamily: state.formDetails?.selectedFamily,
      selectedClass: state.formDetails?.selectedClass,
      nutrientType: state.formDetails?.nutrientType,
      autoCalcName: state.formDetails?.nutrientAutoCalcName ?? ""
    };
    const creating = !nutrient?.key;
    setBusy(true);
    return execute(
      nutrient?.key
        ? `${resources.nutrient(nutrient?.key)}`
        : `${resources.nutrients()}/create`,
      nutrient?.key ? methods.PUT : methods.POST,
      formValues
    )
      .then((response: Nutrient) => {
        dispatch({
          type: NutrientDetailsFormActionType.CHANGE_FORM_STATE,
          payload: {
            key: NutrientDetailsFormActionPayloadType.VALIDATION_DETAILS,
            value: {
              nutrientNameValidationMessage:
                response.nameValidationMessage ?? "",
              nutrientCodeValidationMessage:
                response.codeValidationMessage ?? "",
              nutrientUnitValidationMessage:
                response.unitValidationMessage ?? "",
              nutrientNumeratorValidationMessage:
                response.numeratorValidationMessage ?? "",
              nutrientDenominatorValidationMessage:
                response.denominatorValidationMessage ?? "",
              nutrientOvsNameValidationMessage:
                response.nutrientOvsNameValidationMessage ?? "",
              nutrientSelectedClassValidationMessage:
                response.selectedClassValidationMessage ?? "",
              nutrientAutoCalcNameValidationMessage:
                response.autoCalcNameValidationMessage ?? ""
            }
          }
        });
        if (response?.isInvalid) {
          setSuccess(false);
          return;
        }
        reset(
          creating
            ? { name: "", code: "" }
            : { name: response.name ?? "", code: response.code ?? "" }
        );
        if (!creating) {
          onNameSaved?.(response.name ?? formValues.name ?? "");
        }
        dispatch({
          type: NutrientDetailsFormActionType.ON_PAGE_LOAD,
          payload: creating
            ? {
                isFormDirty: false,
                formDetails: {
                  nutrientName: "",
                  nutrientCode: "",
                  nutrientUnit: "",
                  nutrientOvsName: "",
                  nutrientUnitKey: "",
                  numeratorNutrientKey: "",
                  denominatorNutrientKey: "",
                  isRatio: false,
                  nutrientPackages: [],
                  selectedFamily: "",
                  selectedClass: "",
                  nutrientType: undefined,
                  nutrientAutoCalcName: ""
                },
                validationDetails: {
                  nutrientNameValidationMessage: "",
                  nutrientCodeValidationMessage: "",
                  nutrientUnitValidationMessage: "",
                  nutrientNumeratorValidationMessage: "",
                  nutrientDenominatorValidationMessage: "",
                  nutrientOvsNameValidationMessage: "",
                  nutrientSelectedClassValidationMessage: "",
                  nutrientAutoCalcNameValidationMessage: ""
                },
                key: "",
                ratioUnitKey: response?.ratioUnitKey ?? state.ratioUnitKey,
                nutrientUnits: response?.unitOptions,
                nutrientPackageOptions: response?.nutrientPackageOptions,
                nutrientOptions: response?.nutrientOptions,
                nutrientDetailsDropdownOptions:
                  response?.nutrientDetailsDropdownOptions,
                nutrientTypeOptions: response?.nutrientTypeOptions
              }
            : {
                key: response?.key,
                formDetails: {
                  nutrientName: response?.name ?? "",
                  nutrientCode: response?.code ?? "",
                  nutrientOvsName: response?.ovsName ?? "",
                  nutrientUnit: response?.unit ?? "",
                  nutrientUnitKey: response?.unitKey ?? "",
                  numeratorNutrientKey:
                    response?.numeratorNutrientKey ?? undefined,
                  denominatorNutrientKey:
                    response?.denominatorNutrientKey ?? undefined,
                  nutrientPackages: response?.nutrientPackages ?? [],
                  isRatio: response?.isRatio,
                  selectedFamily: response?.selectedFamily,
                  selectedClass: response?.selectedClass,
                  nutrientAutoCalcName: response?.autoCalcName ?? "",
                  nutrientType: response?.nutrientType
                },
                validationDetails: {
                  nutrientNameValidationMessage: "",
                  nutrientCodeValidationMessage: "",
                  nutrientUnitValidationMessage: "",
                  nutrientNumeratorValidationMessage: "",
                  nutrientDenominatorValidationMessage: "",
                  nutrientSelectedClassValidationMessage: "",
                  nutrientOvsNameValidationMessage: "",
                  nutrientAutoCalcNameValidationMessage: ""
                } as ValidationDetails,
                nutrientUnits: response?.unitOptions,
                ratioUnitKey: response?.ratioUnitKey,
                nutrientPackageOptions: response?.nutrientPackageOptions,
                nutrientOptions: response?.nutrientOptions,
                nutrientDetailsDropdownOptions:
                  response?.nutrientDetailsDropdownOptions,
                nutrientTypeOptions: response?.nutrientTypeOptions
              }
        });
        setSuccess(true);
      })
      .catch((err: any) => {
        // eslint-disable-next-line no-console
        console.error(err);
        setSuccess(false);
        setSaveFailed(true);
      })
      .finally(() => setBusy(false));
  };

  const handleClose = (
    event: React.SyntheticEvent<any> | Event,
    reason?: SnackbarCloseReason
  ) => {
    if (reason === "clickaway") {
      return;
    }
    setSuccess(false);
    setSaveFailed(false);
  };

  return (
    <div
      className={
        nutrient?.key
          ? styles.masterContainerEdit
          : styles.masterContainerCreate
      }
    >
      <ConfirmationModal
        isOpen={confirmationModalIsOpen}
        setIsOpen={setConfirmationModalIsOpen}
        callback={onSave}
      />
      <Paper className={styles.pageContent}>
        <form className={styles.form}>
          {/* First Row: form heading */}
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }} className={styles.spaceChildrenApart}>
              <Header2>{t`__NUTRIENT_DETAILS_SUBTITLE`}</Header2>
              <Snackbar
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
                open={success || saveFailed}
                autoHideDuration={6000}
                onClose={handleClose}
              >
                <Alert
                  elevation={6}
                  variant="filled"
                  onClose={handleClose}
                  severity={saveFailed ? "error" : "success"}
                >
                  {saveFailed
                    ? t`__NUTRIENT_DETAILS_SAVE_FAILED`
                    : t`__NUTRIENT_DETAILS_SAVED`}
                </Alert>
              </Snackbar>

              <PrimaryButton
                disabled={!state.isFormDirty || busy}
                onClick={onClick}
                name="saveNutrient"
              >
                {t`__NUTRIENT_DETAILS_SAVE`}
              </PrimaryButton>
            </Grid>
          </Grid>
          {/* Second Row: name, code */}
          <Grid className={styles.rowOfInputs} container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                name="name"
                control={control}
                rules={{
                  required: t`__NUTRIENT_DETAILS_NAME_REQUIRED`,
                  validate: value =>
                    !!value?.trim() || t`__NUTRIENT_DETAILS_NAME_REQUIRED`
                }}
                render={({ field }) => {
                  const { ref, ...passProps } = field;
                  const validationMessage =
                    errors.name?.message ||
                    state.validationDetails?.nutrientNameValidationMessage;
                  return (
                    <TextField
                      {...passProps}
                      inputRef={ref}
                      fullWidth
                      size="small"
                      variant="outlined"
                      label={t`__NUTRIENT_DETAILS_NAME`}
                      error={!!validationMessage}
                      helperText={validationMessage}
                      onBlur={() => {
                        field.onBlur();
                        void trigger("name");
                      }}
                      onChange={event => {
                        field.onChange(event);
                        dispatch({
                          type: NutrientDetailsFormActionType.CHANGE_FORM_STATE,
                          payload: {
                            key: NutrientDetailsFormActionPayloadType.FORM_DETAILS,
                            value: {
                              ...state.formDetails,
                              nutrientName: event.target.value
                            }
                          }
                        });
                      }}
                    />
                  );
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                name="code"
                control={control}
                rules={{
                  required: t`__NUTRIENT_DETAILS_CODE_REQUIRED`,
                  validate: value =>
                    !!value?.trim() || t`__NUTRIENT_DETAILS_CODE_REQUIRED`
                }}
                render={({ field }) => {
                  const { ref, ...passProps } = field;
                  const validationMessage =
                    errors.code?.message ||
                    state.validationDetails?.nutrientCodeValidationMessage;
                  return (
                    <TextField
                      {...passProps}
                      inputRef={ref}
                      fullWidth
                      size="small"
                      variant="outlined"
                      label={t`__NUTRIENT_DETAILS_CODE`}
                      error={!!validationMessage}
                      helperText={validationMessage}
                      onBlur={() => {
                        field.onBlur();
                        void trigger("code");
                      }}
                      onChange={event => {
                        field.onChange(event);
                        dispatch({
                          type: NutrientDetailsFormActionType.CHANGE_FORM_STATE,
                          payload: {
                            key: NutrientDetailsFormActionPayloadType.FORM_DETAILS,
                            value: {
                              ...state.formDetails,
                              nutrientCode: event.target.value
                            }
                          }
                        });
                      }}
                    />
                  );
                }}
              />
            </Grid>
          </Grid>
          {/* third Row: AutocalcName and Type */}
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                label={t`__NUTRIENT_DETAILS_AUTOCALC_NAME`}
                error={
                  state.validationDetails
                    ?.nutrientAutoCalcNameValidationMessage !== undefined &&
                  state.validationDetails
                    ?.nutrientAutoCalcNameValidationMessage !== ""
                }
                helperText={
                  state.validationDetails?.nutrientAutoCalcNameValidationMessage
                }
                onChange={e => {
                  dispatch({
                    type: NutrientDetailsFormActionType.CHANGE_FORM_STATE,
                    payload: {
                      key: NutrientDetailsFormActionPayloadType.FORM_DETAILS,
                      value: {
                        ...state.formDetails,
                        nutrientAutoCalcName: e.target.value
                      }
                    }
                  });
                }}
                value={state.formDetails?.nutrientAutoCalcName || ""}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Autocomplete
                options={state?.nutrientTypeOptions ?? []}
                getOptionLabel={option => option.displayName ?? ""}
                value={
                  state?.nutrientTypeOptions?.find(
                    n => n.key === state.formDetails?.nutrientType
                  ) ?? null
                }
                onChange={(e, value) => {
                  dispatch({
                    type: NutrientDetailsFormActionType.CHANGE_FORM_STATE,
                    payload: {
                      key: NutrientDetailsFormActionPayloadType.FORM_DETAILS,
                      value: {
                        ...state.formDetails,
                        nutrientType: value?.key ?? undefined
                      }
                    }
                  });
                }}
                renderInput={params => (
                  <TextField
                    {...params}
                    variant="outlined"
                    style={{ marginLeft: 0 }}
                    size="small"
                    label={t`__NUTRIENT_DETAILS_TYPE`}
                  />
                )}
              />
            </Grid>
          </Grid>
          {/* fourth Row: unit and  OVSName*/}
          <Grid container direction="row" spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Autocomplete
                id="nutrient-unit-select"
                options={state?.nutrientUnits ?? []}
                getOptionLabel={option => option.displayValue ?? ""}
                value={
                  state?.nutrientUnits?.find(
                    n => n.keyValue === state.formDetails?.nutrientUnitKey
                  ) ?? null
                }
                onChange={(e, value) => {
                  const isRatio = value?.keyValue === state.ratioUnitKey;
                  dispatch({
                    type: NutrientDetailsFormActionType.CHANGE_FORM_STATE,
                    payload: {
                      key: NutrientDetailsFormActionPayloadType.FORM_DETAILS,
                      value: {
                        ...state.formDetails,
                        nutrientUnitKey: value?.keyValue ?? "",
                        nutrientUnit: value?.displayValue ?? "",
                        isRatio,
                        numeratorNutrientKey: isRatio
                          ? state.formDetails?.numeratorNutrientKey
                          : undefined,
                        denominatorNutrientKey: isRatio
                          ? state.formDetails?.denominatorNutrientKey
                          : undefined
                      }
                    }
                  });
                }}
                renderInput={params => (
                  <TextField
                    {...params}
                    variant="outlined"
                    style={{ marginLeft: 0 }}
                    size="small"
                    label={t`__NUTRIENT_DETAILS_UNIT`}
                    error={
                      state.validationDetails?.nutrientUnitValidationMessage !==
                      ""
                    }
                  />
                )}
              />
              <FormHelperText
                variant="outlined"
                error={
                  state.validationDetails?.nutrientUnitValidationMessage !== ""
                }
              >
                {state.validationDetails?.nutrientUnitValidationMessage}
              </FormHelperText>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                label={t`__NUTRIENT_DETAILS_OVS_NAME`}
                error={
                  state.validationDetails?.nutrientOvsNameValidationMessage !==
                    undefined &&
                  state.validationDetails?.nutrientOvsNameValidationMessage !==
                    ""
                }
                helperText={
                  state.validationDetails?.nutrientOvsNameValidationMessage
                }
                onChange={e => {
                  dispatch({
                    type: NutrientDetailsFormActionType.CHANGE_FORM_STATE,
                    payload: {
                      key: NutrientDetailsFormActionPayloadType.FORM_DETAILS,
                      value: {
                        ...state.formDetails,
                        nutrientOvsName: e.target.value
                      }
                    }
                  });
                }}
                value={state.formDetails?.nutrientOvsName || ""}
              />
            </Grid>
          </Grid>
          {/* fifth Row: numerator and  denominator*/}
          {state.formDetails?.isRatio && (
            <Grid container direction="row" spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Autocomplete
                  id="numerator-nutrients-select"
                  options={state?.nutrientOptions ?? []}
                  getOptionLabel={option => option.displayValue ?? ""}
                  value={
                    state?.nutrientOptions?.find(
                      n =>
                        n.keyValue === state.formDetails?.numeratorNutrientKey
                    ) ?? null
                  }
                  onChange={(e, value) => {
                    dispatch({
                      type: NutrientDetailsFormActionType.CHANGE_FORM_STATE,
                      payload: {
                        key: NutrientDetailsFormActionPayloadType.FORM_DETAILS,
                        value: {
                          ...state.formDetails,
                          numeratorNutrientKey: value?.keyValue
                        }
                      }
                    });
                  }}
                  renderInput={params => (
                    <TextField
                      {...params}
                      variant="outlined"
                      placeholder={t`__NUTRIENT_DETAILS_NUMERATOR_PLACEHOLDER`}
                      size="small"
                      label={t`__NUTRIENT_DETAILS_NUMERATOR`}
                      error={
                        state.validationDetails
                          ?.nutrientNumeratorValidationMessage !== ""
                      }
                    />
                  )}
                />
                <FormHelperText
                  variant="outlined"
                  error={
                    state.validationDetails
                      ?.nutrientNumeratorValidationMessage !== ""
                  }
                >
                  {state.validationDetails?.nutrientNumeratorValidationMessage}
                </FormHelperText>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Autocomplete
                  id="denominator-nutrients-select"
                  options={state?.nutrientOptions ?? []}
                  getOptionLabel={option => option.displayValue ?? ""}
                  value={
                    state?.nutrientOptions?.find(
                      n =>
                        n.keyValue === state.formDetails?.denominatorNutrientKey
                    ) ?? null
                  }
                  onChange={(e, value) => {
                    dispatch({
                      type: NutrientDetailsFormActionType.CHANGE_FORM_STATE,
                      payload: {
                        key: NutrientDetailsFormActionPayloadType.FORM_DETAILS,
                        value: {
                          ...state.formDetails,
                          denominatorNutrientKey: value?.keyValue
                        }
                      }
                    });
                  }}
                  renderInput={params => (
                    <TextField
                      {...params}
                      variant="outlined"
                      style={{ marginLeft: 0 }}
                      placeholder={t`__NUTRIENT_DETAILS_DENOMINATOR_PLACEHOLDER`}
                      size="small"
                      label={t`__NUTRIENT_DETAILS_DENOMINATOR`}
                      error={
                        state.validationDetails
                          ?.nutrientDenominatorValidationMessage !== ""
                      }
                    />
                  )}
                />
                <FormHelperText
                  variant="outlined"
                  error={
                    state.validationDetails
                      ?.nutrientDenominatorValidationMessage !== ""
                  }
                >
                  {
                    state.validationDetails
                      ?.nutrientDenominatorValidationMessage
                  }
                </FormHelperText>
              </Grid>
            </Grid>
          )}
          {/* sixth Row: family and class */}
          <Grid container direction="row" spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Autocomplete
                id="family-select"
                options={state?.nutrientDetailsDropdownOptions?.families ?? []}
                getOptionLabel={option => option.name ?? ""}
                value={
                  state?.nutrientDetailsDropdownOptions?.families?.find(
                    n => n.keyValue === state.formDetails?.selectedFamily
                  ) ?? null
                }
                onChange={(e, value) => {
                  dispatch({
                    type: NutrientDetailsFormActionType.CHANGE_FORM_STATE,
                    payload: {
                      key: NutrientDetailsFormActionPayloadType.FORM_DETAILS,
                      value: {
                        ...state.formDetails,
                        selectedFamily: value?.keyValue,
                        selectedClass: undefined
                      }
                    }
                  });
                }}
                renderInput={params => (
                  <TextField
                    {...params}
                    variant="outlined"
                    placeholder={t`__NUTRIENT_DETAILS_FAMILY_PLACEHOLDER`}
                    size="small"
                    label={t`__NUTRIENT_DETAILS_FAMILY`}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Autocomplete
                id="class-select"
                options={classOptions ?? []}
                disabled={!state.formDetails?.selectedFamily}
                getOptionLabel={option => option.name ?? ""}
                value={
                  classOptions?.find(
                    n => n.keyValue === state.formDetails?.selectedClass
                  ) ?? null
                }
                onChange={(e, value) => {
                  dispatch({
                    type: NutrientDetailsFormActionType.CHANGE_FORM_STATE,
                    payload: {
                      key: NutrientDetailsFormActionPayloadType.FORM_DETAILS,
                      value: {
                        ...state.formDetails,
                        selectedClass: value?.keyValue
                      }
                    }
                  });
                }}
                renderInput={params => (
                  <TextField
                    {...params}
                    variant="outlined"
                    style={{ marginLeft: 0 }}
                    placeholder={t`__NUTRIENT_DETAILS_CLASS_PLACEHOLDER`}
                    size="small"
                    label={t`__NUTRIENT_DETAILS_CLASS`}
                    error={
                      state.validationDetails
                        ?.nutrientSelectedClassValidationMessage !== ""
                    }
                  />
                )}
              />
              <FormHelperText
                variant="outlined"
                error={
                  state.validationDetails
                    ?.nutrientSelectedClassValidationMessage !== ""
                }
              >
                {
                  state.validationDetails
                    ?.nutrientSelectedClassValidationMessage
                }
              </FormHelperText>
            </Grid>
          </Grid>
          {/* seventh Row: packages */}
          <Grid className={styles.nutrientPackages} size={{ xs: 12 }}>
            <FormControl fullWidth>
              <InputLabel
                shrink
                htmlFor="nutrientPackages"
                className={styles.labelMultipleSelect}
              >
                &nbsp;&nbsp;{t`__NUTRIENT_DETAILS_NUTRIENT_PACKAGES`}
                <Tooltip title={t`__NUTRIENT_DETAILS_SELECTOR_CTRL_HINT`}>
                  <IconButton className={styles.multiSelectInfoTool}>
                    <InfoIcon
                      iconSize={12}
                      color={theme.palette.primary.fsBlue}
                    />
                  </IconButton>
                </Tooltip>
              </InputLabel>
              <Select<string[]>
                multiple
                native
                label="nutrientPackages"
                variant="outlined"
                value={state.formDetails?.nutrientPackages ?? [""]}
                onChange={event => {
                  if (!nutrient?.key) {
                    // @ts-ignore Typings are not considering `native`
                    handleMultipleSelect(event);
                  } else {
                    // @ts-ignore Typings are not considering `native`
                    handleMultipleSelectAppendOnly(event);
                  }
                }}
              >
                {state?.nutrientPackageOptions?.map(pkg => (
                  <option key={pkg.keyValue} value={pkg.keyValue}>
                    {pkg.displayValue}
                  </option>
                ))}
              </Select>
            </FormControl>
            <small className={styles.reminderText}>
              {pageLoadState.loaded
                ? t`__NUTRIENT_DETAILS_NUTRIENT_PACKAGES_HELPER`
                : ""}
            </small>
          </Grid>
        </form>
      </Paper>
    </div>
  );
};

export default NutrientsDetailsForm;
